import React, { useContext, useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, orderBy, query } from 'firebase/firestore';
import { FiArrowRight, FiCreditCard, FiDollarSign, FiShoppingBag } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import layoutStyles from '../styles/layout.module.css';
import styles from './Transactions.module.css';
import { UserContext } from '../App';
import { db } from '../firebase';
import { formatMoney, fromMinorUnits } from '../utils/currency';

function formatDate(value) {
  if (!value?.toDate) return '—';
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(value.toDate());
}

export default function Transactions() {
  const appUser = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!appUser?.id) return;
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const snap = await getDocs(query(collection(db, 'users', appUser.id, 'payments'), orderBy('createdAt', 'desc')));
        const payments = await Promise.all(snap.docs.map(async (docSnap) => {
          const payment = { id: docSnap.id, ...(docSnap.data() || {}) };
          const orderSnap = payment.orderId ? await getDoc(doc(db, 'orders', String(payment.orderId))) : null;
          return {
            ...payment,
            order: orderSnap?.exists() ? { id: orderSnap.id, ...(orderSnap.data() || {}) } : null,
          };
        }));
        if (active) setRows(payments);
      } catch {
        if (active) setRows([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [appUser?.id]);

  const countLabel = useMemo(() => `${rows.length} transaction${rows.length === 1 ? '' : 's'}`, [rows.length]);

  return (
    <div className={layoutStyles.detailPage}>
      <TopBar
        variant="back"
        backLabel="Back"
        onBack={() => (location.state?.backTo ? navigate(location.state.backTo) : navigate('/fan/settings'))}
        rightActionLabel="Home"
        onRightAction={() => navigate('/home')}
      />
      <div className={`${layoutStyles.detailMain} ${styles.pageShell}`.trim()}>
        <section className={styles.heroCard}>
          <div>
            <span className={styles.eyebrow}>Transactions</span>
            <h1>Drop transactions</h1>
            <p>Every primary and resale drop purchase you made, with totals, fees, and payment references.</p>
          </div>
          <div className={styles.heroStat}>
            <span>Total</span>
            <strong>{loading ? '…' : countLabel}</strong>
          </div>
        </section>

        <section className={styles.listCard}>
          {loading ? <p className={styles.state}>Loading transactions…</p> : null}
          {!loading && !rows.length ? <p className={styles.state}>No drop transactions yet.</p> : null}
          {!loading && rows.length ? (
            <div className={styles.list}>
              {rows.map((row) => {
                const order = row.order || {};
                const amountMinor = Number(order.amounts?.totalMinor ?? order.amounts?.grossMinor ?? row.amountTotalMinor ?? 0);
                const currency = String(order.amounts?.currency || row.currency || 'USD');
                const unitId = String(order.unitIds?.[0] || '').trim();
                const typeLabel = String(order.listingId || '').startsWith('primary_') ? 'Primary purchase' : 'Resale purchase';
                const title = String(row.productTitle || order.productSnapshot?.title || row.productId || row.orderId || row.id);
                return (
                  <button key={row.id} type="button" className={styles.listItem} onClick={() => navigate(`/transactions/${row.orderId || row.id}`, { state: { unitId, backTo: '/transactions' } })}>
                    <div className={styles.itemIcon}><FiShoppingBag /></div>
                    <div className={styles.itemCopy}>
                      <strong>{title}</strong>
                      <div className={styles.itemMeta}>
                        <span><FiCreditCard /> {typeLabel}</span>
                        <span><FiDollarSign /> {formatMoney(fromMinorUnits(amountMinor, currency), currency)}</span>
                        <span>{formatDate(row.createdAt)}</span>
                      </div>
                    </div>
                    <FiArrowRight className={styles.arrow} />
                  </button>
                );
              })}
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
